import { ConfettiModal } from '@design-system/components/modal/confettiModal';
import { Typography } from '@design-system/components/typography/typography';
import {
  CreatorLink,
  CreatorLinkProps,
} from '../../creator/home/components/CreatorLink';
import { ProgramCard } from './ProgramCard';
import * as styles from './SuccessModal.css';
import { useState } from 'react';
import { Col, Row } from '@design-system/components/grid/grid';
import { Button } from '@design-system/components/button/button';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { CopyCard } from '@design-system/components/modals/CopyLinkModal';

type OTPSuccessModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  showDismiss?: boolean;
  details?: string;
  links?: Array<CreatorLinkProps>;
  bottomText?: string;
  collapsibleLinks?: boolean;
  bottomComponent?: React.ReactNode;
  programData?: {
    title: string;
    subtitle: string;
    thumbnailUrl: string;
    price: string;
    discountedPrice: string | null;
  };
  cta?: Array<{
    className: string;
    label: string;
    onClick: () => void;
    variant?: 'text' | 'outlined' | undefined;
  }>;
};

export const SuccessModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  showDismiss,
  details,
  bottomComponent,
  programData,
  cta,
  ...p
}: OTPSuccessModalProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const links =
    p?.links && p.collapsibleLinks && isCollapsed
      ? p.links.slice(0, 1)
      : p.links;
  return (
    <ConfettiModal
      isOpen={isOpen}
      onClose={onClose}
      modalClassName="min-h-[400px]"
    >
      <div className={styles.wrapper}>
        <Typography variant="display" size="small">
          {title}
        </Typography>
        {!subtitle ? null : (
          <Typography variant="body" size="large">
            {subtitle}
          </Typography>
        )}

        {programData && isCollapsed ? (
          <Col>
            <ProgramCard {...programData} />
          </Col>
        ) : null}

        {!details ? null : <Typography variant="title">{details}</Typography>}

        {!links ? null : (
          <Col>
            {links.map((l) => (
              <CopyCard key={l.url} url={l.url} linkTitle={l.title}>
                {l.desc}
              </CopyCard>
            ))}
          </Col>
        )}
        {!p.bottomText ? null : (
          <Typography variant="body" size="large" fontWeight="normal">
            {p.bottomText}
          </Typography>
        )}
        {p.collapsibleLinks && links ? (
          <Button
            onClick={() => setIsCollapsed(!isCollapsed)}
            variant="text"
            color="neutral"
            rightIcon={isCollapsed ? faChevronDown : faChevronUp}
          >
            <Typography variant="body" size="medium">
              {isCollapsed ? 'More links' : 'Hide'}
            </Typography>
          </Button>
        ) : null}
        {!cta ? null : (
          <div className="flex justify-center items-center gap-2">
            {cta.map((item) => (
              <Button
                key={item.label}
                onClick={item.onClick}
                color="neutral"
                variant={item.variant || 'filled'}
                className={item.className}
              >
                {item.label}
              </Button>
            ))}
          </div>
        )}
        {bottomComponent || null}
        {showDismiss ? (
          <Row justifyCenter>
            <Button onClick={onClose} color="neutral" style={{ minWidth: 120 }}>
              {`Dismiss`}
            </Button>
          </Row>
        ) : null}
      </div>
    </ConfettiModal>
  );
};
